import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import * as isBetween from 'dayjs/plugin/isBetween';
import { GlobalService } from 'src/app/api/global.service';
import { ProjectsService } from 'src/app/api/projects.service';

dayjs.extend(isBetween);

// The tasks table in selection mode, for choosing the task something else (e.g. a time entry) is for. Loads the tasks the
// same way the Tasks page does. Dismisses with { task, project } for the chosen task, or null.
@Component({
  selector: 'app-task-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
})
export class TaskSelectComponent implements OnInit {
  // The contact / company the entry is for - only their tasks are shown
  @Input() client_guid = '';
  @Input() company_guid = '';
  @Input() client_name = '';

  // The tasks table, which does the quick search
  @ViewChild('taskTable') taskTable: any;

  tasks: any[] | null = null;
  loaded_tasks = false;
  background_loading = false;
  tasks_view = 'all';
  // How many tasks can be picked
  readonly max_allowed_selections = 1;
  selected: any[] = [];

  pendingStartDate = dayjs().subtract(90, 'day').format('YYYY-MM-DD');
  pendingEndDate = dayjs().add(30, 'day').format('YYYY-MM-DD');
  private current_start_date = dayjs(this.pendingStartDate).startOf('day').toDate();
  private current_end_date = dayjs(this.pendingEndDate).endOf('day').toDate();
  private load_generation = 0;
  url_mod = '';

  constructor(private modalController: ModalController, public globalService: GlobalService, private projectService: ProjectsService) { }

  ngOnInit() {
    if (this.client_guid) this.url_mod = `&client=${this.client_guid}`;
    else if (this.company_guid) this.url_mod = `&company=${this.company_guid}`;
    this.GetData();
  }

  get canApplyDates(): boolean {
    return dayjs(this.pendingStartDate).startOf('day').valueOf() !== this.current_start_date.getTime()
      || dayjs(this.pendingEndDate).endOf('day').valueOf() !== this.current_end_date.getTime();
  }

  applyDateFilter() {
    if (!this.pendingStartDate || !this.pendingEndDate) return;
    this.current_start_date = dayjs(this.pendingStartDate).startOf('day').toDate();
    this.current_end_date = dayjs(this.pendingEndDate).endOf('day').toDate();
    this.GetData();
  }

  SetView(view: string) {
    this.tasks_view = view;
    this.selected = [];
  }

  // Pulls the tasks in the date range out of a chunk of projects; each task carries its project (for its client and title)
  private ExtractTasks(projects: any[]): any[] {
    const result: any[] = [];
    const range_start = dayjs(this.current_start_date);
    const range_end = dayjs(this.current_end_date);
    for (const project of (projects || [])) {
      let project_clone: any = null;
      for (const task of (project.tasks || [])) {
        const start = dayjs(task.start_date || task.due_date);
        const end = dayjs(task.due_date);
        const in_range = start.isBetween(range_start, range_end, null, '[]') || end.isBetween(range_start, range_end, null, '[]')
          || (start.isBefore(range_start) && end.isAfter(range_end));
        if (!in_range) continue;
        if (!project_clone) project_clone = JSON.parse(JSON.stringify(project));
        result.push({ ...task, client: project.client, company: project.company, client_guid: project.client_guid, company_guid: project.company_guid, project: project_clone });
      }
    }
    return result;
  }

  async GetData() {
    this.load_generation++;
    const generation = this.load_generation;
    const all: any[] = [];
    this.tasks = null;
    this.selected = [];
    this.loaded_tasks = false;
    this.background_loading = false;
    await this.projectService.GetProjectsChunked(
      this.current_start_date.toISOString(),
      this.current_end_date.toISOString(),
      this.url_mod,
      async (chunk_projects, meta) => {
        if (generation !== this.load_generation) return;
        all.push(...this.ExtractTasks(chunk_projects));
        this.tasks = [...all];
        this.loaded_tasks = true;
        this.background_loading = !meta.is_last;
      },
      () => generation !== this.load_generation
    );
    if (generation !== this.load_generation) return;
    this.background_loading = false;
    this.loaded_tasks = true;
  }

  // One task can be chosen - once it is ticked the other checkboxes are disabled (see the task table's max_allowed_selections)
  OnSelectionChange(tasks: any[]) {
    this.selected = tasks || [];
  }

  Select() {
    const task = this.selected[0];
    this.modalController.dismiss(task ? { task, project: task.project } : null);
  }

  Close() {
    this.modalController.dismiss(null);
  }
}
